/**
 * CorrelationRenderer - Renderer for correlation/client tracking settings
 *
 * Allows setting a client tracking ID expression for correlating workflow runs.
 */

import { makeStyles, tokens, Text, Input, Field } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import type { CorrelationSettings } from '@/types/nodeSettings';

import {
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  header: settingHeaderStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export interface CorrelationRendererProps extends SettingRendererProps<CorrelationSettings> {
  label?: string;
  description?: string;
}

/**
 * CorrelationRenderer component for client tracking ID configuration
 */
export const CorrelationRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Correlation',
  description = 'Configure client tracking ID for correlating workflow runs',
}: CorrelationRendererProps) => {
  const styles = useStyles();

  if (!setting.isSupported) {
    return null;
  }

  const currentValue = setting.value ?? {};

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      onChange({ clientTrackingId: value });
    } else {
      onChange(undefined);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.label}>{label}</Text>
      </div>
      <Text className={styles.description}>{description}</Text>
      <div className={styles.inputContainer}>
        <Field label="Client Tracking ID">
          <Input
            value={currentValue.clientTrackingId ?? ''}
            onChange={handleChange}
            placeholder="@guid()"
            disabled={readOnly}
            aria-label="Client tracking ID expression"
          />
        </Field>
        <Text className={styles.hint}>
          Expression to generate unique tracking ID (e.g., @guid())
        </Text>
      </div>
    </div>
  );
};

CorrelationRenderer.displayName = 'CorrelationRenderer';

registerSettingRenderer('correlation', CorrelationRenderer as React.ComponentType<SettingRendererProps<unknown>>);
